/*
 * @Author: Alex
 * @Date: 2023-05-17 10:28:57
 * @Description:
 */
import { BaseMapTool } from './mapToolBase'

import Draw from 'ol/interaction/Draw'
import { Feature, Map } from 'ol'
import Overlay from 'ol/Overlay'
import { Geometry, LineString, Polygon } from 'ol/geom'
import { getArea, getLength } from 'ol/sphere'
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style'
import { Vector as VectorSource } from 'ol/source'
import { Vector as VectorLayer } from 'ol/layer'
import { unByKey } from 'ol/Observable'
import { getNanoId } from '@vmap/core'

interface MapMeasureEvent {
  [_: string]: any
}

type AreaUnit = 'm2' | 'km2' | 'auto'
type LengthUnit = 'meter' | 'kilometer' | 'auto'

export interface MapMeasureOptions {
  /**
   * 指定面积单位 auto 则会自动判断
   */
  areaUnit?: AreaUnit
  /**
   * 指定长度单位 auto 则会自动判断
   */
  lengthUnit?: LengthUnit
}

/**
 * Message to show when the user is drawing a polygon.
 * @type {string}
 */
const continuePolygonMsg = '点击继续绘制多边形'

/**
 * Message to show when the user is drawing a line.
 * @type {string}
 */
const continueLineMsg = '点击继续绘制线'

/**
 * Format length output.
 * @param {LineString} line The line.
 * @return {string} The formatted length.
 */
const formatLength = (line: Geometry) => {
  const length = getLength(line)
  let output
  if (length > 100) {
    output = Math.round((length / 1000) * 100) / 100 + ' ' + 'km'
  } else {
    output = Math.round(length * 100) / 100 + ' ' + 'm'
  }
  return output
}

/**
 * Format area output.
 * @param {Polygon} polygon The polygon.
 * @return {string} Formatted area.
 */
const formatArea = (polygon: Geometry) => {
  const area = getArea(polygon)
  let output
  if (area > 10000) {
    output = Math.round((area / 1000000) * 100) / 100 + ' ' + 'km<sup>2</sup>'
  } else {
    output = Math.round(area * 100) / 100 + ' ' + 'm<sup>2</sup>'
  }
  return output
}

/**
 * 地图量算工具
 * - 量算面积
 * - 量算长度
 * - 清除等
 * - 自定义样式
 */
export default class MapMeasureTool extends BaseMapTool<MapMeasureEvent> {
  // private _areaUnit: AreaUnit = 'auto'

  // private _lengthUnit: LengthUnit = 'auto'

  private _sketch!: Feature | null

  private _source = new VectorSource()

  /**
   * 存放结果的图层
   */
  private _vectorLayer!: any

  private _layerId = `MeasureLayer_${getNanoId()}`

  /**
   * The help tooltip element.
   * @type {HTMLElement}
   */
  private _helpTooltipElement!: HTMLElement | null

  private _helpTooltip!: Overlay

  /**
   * 测量结果div
   * The measure tooltip element.
   * @type {HTMLElement}
   */
  private _measureTooltipElement!: HTMLElement | null

  /**
   * 测量结果 overlay
   * Overlay to show the measurement.
   * @type {Overlay}
   */
  private _measureTooltip!: Overlay

  /**
   * so we can remove it later
   */
  private _draw!: Draw

  // 所有的 overlay
  private _overlays: Overlay[] = []

  private _pointerMoveListener: any

  constructor(map: Map, options?: MapMeasureOptions) {
    super(map)
    if (!options) {
      options = {}
    }
    // this._areaUnit = options.areaUnit ?? 'auto'
    // this._lengthUnit = options.lengthUnit ?? 'auto'
  }

  doArea() {
    this.clearAndReady(this.map)
    this.addInteraction(this.map, 'Polygon')
  }

  doLength() {
    this.clearAndReady(this.map)
    this.addInteraction(this.map, 'LineString')
  }

  cleanup() {
    const layers = this.map.getAllLayers()
    const lyr = layers.find(lyr => lyr.get('id') === this._layerId)
    if (lyr) {
      this.map.removeLayer(lyr)
    }
    unByKey(this._pointerMoveListener)
    this.map.removeInteraction(this._draw)
    // unset sketch
    this._sketch = null
    // unset tooltip so that a new one can be created
    this._measureTooltipElement = null
    this._helpTooltipElement = null
    this._measureTooltipElement = null
    // 清除 overlay
    this._overlays.forEach(overlay => {
      this.map.removeOverlay(overlay)
    })

    this.map.removeOverlay(this._helpTooltip)
  }

  private clearAndReady(map: Map) {
    this.cleanup()

    this._source = new VectorSource()
    this._vectorLayer = new VectorLayer({
      source: this._source,
      style: {
        'fill-color': 'rgba(255, 255, 255, 0.2)',
        'stroke-color': '#ffcc33',
        'stroke-width': 2,
        'circle-radius': 7,
        'circle-fill-color': '#ffcc33'
      }
    })
    this._vectorLayer.set('id', this._layerId)
    map.addLayer(this._vectorLayer)

    this._pointerMoveListener = map.on('pointermove', this.pointerMoveHandler)
    map.getViewport().addEventListener('mouseout', () => {
      this._helpTooltipElement?.classList.add('hidden')
    })
  }

  private addInteraction(map: Map, type: 'Polygon' | 'LineString') {
    this._draw = new Draw({
      source: this._source,
      type: type,
      style: new Style({
        fill: new Fill({
          color: 'rgba(255, 255, 255, 0.2)'
        }),
        stroke: new Stroke({
          color: 'rgba(240,230,140, 0.7)',
          lineDash: [10, 10],
          width: 2
        }),
        image: new CircleStyle({
          radius: 5,
          stroke: new Stroke({
            color: 'rgba(255,215,0, 0.7)'
          }),
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)'
          })
        })
      })
    })
    map.addInteraction(this._draw)

    this.createMeasureTooltip(map)
    this.createHelpTooltip(map)

    let listener: any
    this._draw.on('drawstart', (evt: any) => {
      // set sketch
      this._sketch = evt.feature

      /** @type {import("../src/ol/coordinate.js").Coordinate|undefined} */
      let tooltipCoord = evt.coordinate

      listener = this._sketch?.getGeometry()?.on('change', evt => {
        const geom = evt.target
        let output = ''
        if (geom instanceof Polygon) {
          output = formatArea(geom)
          tooltipCoord = geom.getInteriorPoint().getCoordinates()
        } else if (geom instanceof LineString) {
          output = formatLength(geom)
          tooltipCoord = geom.getLastCoordinate()
        }
        if (this._measureTooltipElement) {
          this._measureTooltipElement.innerHTML = output
          this._measureTooltipElement.style.color = '#FFFACD'
        }
        this._measureTooltip.setPosition(tooltipCoord)
      })
    })

    this._draw.on('drawend', () => {
      if (this._measureTooltipElement) {
        this._measureTooltipElement.className = 'ol-tooltip ol-tooltip-static'
      }

      this._measureTooltip.setOffset([0, -7])
      // unset sketch
      this._sketch = null
      // unset tooltip so that a new one can be created
      this._measureTooltipElement = null
      // 创建结果 overlay
      this.createMeasureTooltip(map)
      unByKey(listener)
    })
  }

  /**
   * Creates a new help tooltip
   */
  private createHelpTooltip(map: Map) {
    if (this._helpTooltipElement) {
      this._helpTooltipElement.parentNode?.removeChild(this._helpTooltipElement)
    }
    this._helpTooltipElement = document.createElement('div')
    this._helpTooltipElement.className = 'ol-tooltip hidden'
    this._helpTooltip = new Overlay({
      element: this._helpTooltipElement,
      offset: [15, 0],
      positioning: 'center-left'
    })
    map.addOverlay(this._helpTooltip)
  }

  /**
   * Creates a new measure tooltip
   */
  private createMeasureTooltip(map: Map) {
    if (this._measureTooltipElement) {
      this._measureTooltipElement.parentNode?.removeChild(
        this._measureTooltipElement
      )
    }
    this._measureTooltipElement = document.createElement('div')
    this._measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure'
    this._measureTooltip = new Overlay({
      element: this._measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center',
      stopEvent: false,
      insertFirst: false
    })
    map.addOverlay(this._measureTooltip)
    this._overlays.push(this._measureTooltip)
  }

  private pointerMoveHandler(evt: any) {
    if (evt.dragging) {
      return
    }
    /** @type {string} */
    let helpMsg = '点击开始绘制'

    if (this._sketch) {
      const geom = this._sketch.getGeometry()
      if (geom instanceof Polygon) {
        helpMsg = continuePolygonMsg
      } else if (geom instanceof LineString) {
        helpMsg = continueLineMsg
      }
    }
    if (this._helpTooltipElement) {
      this._helpTooltipElement.innerHTML = helpMsg
      this._helpTooltip.setPosition(evt.coordinate)

      this._helpTooltipElement.style.color = '#FFFACD'
      this._helpTooltipElement?.classList.remove('hidden')
    }
  }

  protected startInternal(): void {
    throw new Error('Pls use `doArea()` or `doLength()` instead')
  }
  protected stopInternal(): void {
    this.cleanup()
  }
}
